import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { api, ApiError } from '../lib/api';
import { formatRelativeTime, getStatusBadgeClass, classNames } from '../lib/utils';
import { Dropdown } from '../components/Dropdown';
import {
  Bell,
  Plus,
  CheckCircle,
  AlertCircle,
  X,
  Loader2,
  RefreshCw,
  Edit,
  Trash2,
  Eye,
  Filter,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import type { Alert, AlertRule, CreateAlertRuleRequest, AlertsQueryParams } from '../lib/types';

const METRICS = [
  { value: 'cpu_avg_pct', label: 'CPU Utilization (%)' },
  { value: 'memory_avg_pct', label: 'Memory Utilization (%)' },
  { value: 'disk_avg_pct', label: 'Disk Utilization (%)' },
  { value: 'cost_daily_usd', label: 'Daily Cost (USD)' },
];

const COMPARISONS = [
  { value: 'gt', label: 'Greater than (>)' },
  { value: 'gte', label: 'Greater than or equal (≥)' },
  { value: 'lt', label: 'Less than (<)' },
  { value: 'lte', label: 'Less than or equal (≤)' },
];

export function AlertsPage() {
  const { idToken: accessToken } = useAuth();
  const [activeTab, setActiveTab] = useState<'alerts' | 'rules'>('alerts');
  const [isLoading, setIsLoading] = useState(false);
  const [allAlerts, setAllAlerts] = useState<Alert[]>([]);
  const [rules, setRules] = useState<AlertRule[]>([]);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [filters, setFilters] = useState<AlertsQueryParams>({});
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [editingRule, setEditingRule] = useState<AlertRule | null>(null);
  const [ruleForm, setRuleForm] = useState<CreateAlertRuleRequest>({
    name: '',
    account_id: '',
    resource_type: '',
    metric: 'cpu_avg_pct',
    comparison: 'gt',
    threshold: 80,
    evaluation_minutes: 15,
    notify_channel: 'sns',
  });
  const [ruleFormErrors, setRuleFormErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  const fetchAlerts = useCallback(async () => {
    if (!accessToken) return;
    setIsLoading(true);
    setError('');
    try {
      // The API returns up to 200 matching alerts (no server-side offset for DynamoDB-backed
      // pagination); paging beyond that is done client-side over this batch.
      const res = await api.alerts.list(accessToken, filters);
      setAllAlerts(res.alerts);
      setPage(1);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load alerts');
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, filters]);

  const fetchRules = useCallback(async () => {
    if (!accessToken) return;
    try {
      const res = await api.alerts.rules.list(accessToken);
      setRules(res.rules);
    } catch (err) {
      console.error('Failed to load rules:', err);
    }
  }, [accessToken]);

  useEffect(() => {
    if (activeTab === 'alerts') fetchAlerts();
    else fetchRules();
  }, [activeTab, fetchAlerts, fetchRules]);

  const alerts = useMemo(
    () => allAlerts.slice((page - 1) * pageSize, page * pageSize),
    [allAlerts, page, pageSize],
  );
  const hasNextPage = page * pageSize < allAlerts.length;

  // Rule Modal
  const openAddRuleModal = () => {
    setRuleForm({
      name: '',
      account_id: '',
      resource_type: '',
      metric: 'cpu_avg_pct',
      comparison: 'gt',
      threshold: 80,
      evaluation_minutes: 15,
      notify_channel: 'sns',
    });
    setRuleFormErrors({});
    setEditingRule(null);
    setShowRuleModal(true);
  };

  const openEditRuleModal = (rule: AlertRule) => {
    setRuleForm({
      name: rule.name,
      account_id: rule.account_id || '',
      resource_type: rule.resource_type || '',
      metric: rule.metric,
      comparison: rule.comparison,
      threshold: rule.threshold,
      evaluation_minutes: rule.evaluation_minutes,
      notify_channel: rule.notify_channel,
    });
    setEditingRule(rule);
    setShowRuleModal(true);
  };

  const closeRuleModal = () => {
    setShowRuleModal(false);
    setEditingRule(null);
  };

  const validateRuleForm = () => {
    const errors: Record<string, string> = {};
    if (!ruleForm.name.trim()) errors.name = 'Rule name is required';
    if (!ruleForm.metric) errors.metric = 'Metric is required';
    if (!ruleForm.comparison) errors.comparison = 'Comparison is required';
    if (ruleForm.threshold === undefined || ruleForm.threshold === null) errors.threshold = 'Threshold is required';
    if ((ruleForm.evaluation_minutes ?? 0) < 1) errors.evaluation_minutes = 'Must be at least 1 minute';
    setRuleFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleRuleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateRuleForm()) return;
    setSubmitting(true);
    setError('');

    try {
      const payload: CreateAlertRuleRequest = {
        name: ruleForm.name.trim(),
        account_id: ruleForm.account_id || undefined,
        resource_type: ruleForm.resource_type || undefined,
        metric: ruleForm.metric,
        comparison: ruleForm.comparison,
        threshold: ruleForm.threshold,
        evaluation_minutes: ruleForm.evaluation_minutes,
        notify_channel: ruleForm.notify_channel,
      };

      await api.alerts.rules.create(accessToken!, payload);
      closeRuleModal();
      fetchRules();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to save rule');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteRule = async (ruleId: string) => {
    if (!window.confirm('Delete this alert rule?')) return;
    try {
      await api.alerts.rules.delete(accessToken!, ruleId);
      fetchRules();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to delete rule');
    }
  };

  const handleAcknowledgeAlert = async (alertId: string) => {
    try {
      await api.alerts.acknowledge(accessToken!, alertId);
      setAllAlerts((prev) => prev.map((a) => (a.alert_id === alertId ? { ...a, status: 'acknowledged' as const } : a)));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to acknowledge alert');
    }
  };

  if (isLoading && alerts.length === 0 && rules.length === 0) {
    return (
      <div className="page-alerts">
        <div className="page-header">
          <h1 className="page-title">Alerts</h1>
        </div>
        <div className="card">
          <div className="card-content">
            <div className="empty-state">
              <Loader2 size={48} className="empty-state-icon spin" />
              <h3 className="empty-state-title">Loading...</h3>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-alerts">
      <header className="page-header">
        <h1 className="page-title">Alerts</h1>
        {activeTab === 'rules' && (
          <button className="btn btn-primary" onClick={openAddRuleModal}>
            <Plus size={18} /> Create Rule
          </button>
        )}
      </header>

      {/* Tabs */}
      <div className="tabs" role="tablist" aria-label="Alert views">
        <button
          role="tab"
          className={classNames('tab', { active: activeTab === 'alerts' })}
          onClick={() => setActiveTab('alerts')}
          aria-selected={activeTab === 'alerts'}
        >
          <Bell size={16} /> Alerts
        </button>
        <button
          role="tab"
          className={classNames('tab', { active: activeTab === 'rules' })}
          onClick={() => setActiveTab('rules')}
          aria-selected={activeTab === 'rules'}
        >
          <Bell size={16} /> Rules
        </button>
      </div>

      {error && (
        <div className="login-alert" style={{ marginBottom: 'var(--space-4)' }}>
          <AlertCircle size={16} /> {error}
        </div>
      )}

      {/* Alerts Tab */}
      {activeTab === 'alerts' && (
        <div className="card">
          <div className="card-content" style={{ padding: 'var(--space-4)' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-3)', alignItems: 'center', marginBottom: 'var(--space-4)' }}>
              <div className="form-group" style={{ marginBottom: 0, minWidth: 180 }}>
                <label htmlFor="alert-status" className="form-label">Status</label>
                <select
                  id="alert-status"
                  className="form-select"
                  value={filters.status || ''}
                  onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value as AlertsQueryParams['status'] }))}
                >
                  <option value="">All Statuses</option>
                  <option value="open">Open</option>
                  <option value="acknowledged">Acknowledged</option>
                  <option value="resolved">Resolved</option>
                </select>
              </div>
              <div className="form-group" style={{ marginBottom: 0, minWidth: 180 }}>
                <label htmlFor="alert-account" className="form-label">Account</label>
                <select
                  id="alert-account"
                  className="form-select"
                  value={filters.account_id || ''}
                  onChange={(e) => setFilters((prev) => ({ ...prev, account_id: e.target.value || undefined }))}
                >
                  <option value="">All Accounts</option>
                  {Array.from(new Set(alerts.map((a) => a.account_id))).map((acc) => (
                    <option key={acc} value={acc}>{acc}</option>
                  ))}
                </select>
              </div>
              <button className="btn btn-ghost" onClick={() => setFilters({})}>
                <Filter size={16} /> Clear
              </button>
              <button className="btn btn-ghost btn-icon ml-auto" onClick={fetchAlerts} disabled={isLoading}>
                <RefreshCw size={20} className={isLoading ? 'spin' : ''} />
              </button>
            </div>

            {alerts.length === 0 ? (
              <div className="empty-state">
                <CheckCircle size={64} className="empty-state-icon" style={{ color: 'var(--color-primary)' }} />
                <h3 className="empty-state-title">No alerts found</h3>
                <p className="empty-state-text">All systems operating normally</p>
              </div>
            ) : (
              <>
                <div className="table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Status</th>
                        <th>Rule</th>
                        <th>Account</th>
                        <th>Resource</th>
                        <th>Metric</th>
                        <th>Value</th>
                        <th>Threshold</th>
                        <th>Triggered</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {alerts.map((alert) => (
                        <tr key={alert.alert_id}>
                          <td>
                            <span className={classNames('badge', `badge-${getStatusBadgeClass(alert.status)}`)}>
                              {alert.status}
                            </span>
                          </td>
                          <td>
                            <code style={{ fontSize: 'var(--text-xs)' }}>{alert.rule_id.slice(0, 8)}…</code>
                          </td>
                          <td>
                            <code style={{ fontSize: 'var(--text-xs)' }}>{alert.account_id}</code>
                          </td>
                          <td>
                            {alert.resource_id ? (
                              <code style={{ fontSize: 'var(--text-xs)' }}>{alert.resource_id.slice(0, 40)}…</code>
                            ) : (
                              <span style={{ color: 'var(--slate-400)', fontSize: 'var(--text-xs)' }}>—</span>
                            )}
                          </td>
                          <td>{alert.metric}</td>
                          <td>
                            <span style={{ fontWeight: 600, fontFamily: 'monospace' }}>{alert.observed_value}</span>
                          </td>
                          <td>
                            <span style={{ fontFamily: 'monospace', color: 'var(--slate-600)' }}>{alert.threshold}</span>
                          </td>
                          <td>{formatRelativeTime(alert.triggered_at)}</td>
                          <td>
                            <div style={{ display: 'flex', gap: 'var(--space-1)' }}>
                              {alert.status === 'open' && (
                                <button
                                  className="btn btn-ghost btn-icon btn-sm"
                                  onClick={() => handleAcknowledgeAlert(alert.alert_id)}
                                  aria-label="Acknowledge"
                                >
                                  <CheckCircle size={16} />
                                </button>
                              )}
                              <button className="btn btn-ghost btn-icon btn-sm" aria-label="View details">
                                <Eye size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {allAlerts.length > pageSize && (
                  <div className="card-footer" style={{ justifyContent: 'center' }}>
                    <div style={{ display: 'flex', gap: 'var(--space-1)' }}>
                      <button
                        className="btn btn-ghost btn-icon btn-sm"
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page === 1}
                        aria-label="Previous page"
                      >
                        <ChevronLeft size={16} />
                      </button>
                      <button
                        className="btn btn-ghost btn-icon btn-sm"
                        onClick={() => setPage((p) => p + 1)}
                        disabled={!hasNextPage}
                        aria-label="Next page"
                      >
                        <ChevronRight size={16} />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Rules Tab */}
      {activeTab === 'rules' && (
        <div className="card">
          <div className="card-content">
            {rules.length === 0 ? (
              <div className="empty-state">
                <AlertCircle size={64} className="empty-state-icon" />
                <h3 className="empty-state-title">No alert rules configured</h3>
                <p className="empty-state-text">Create rules to get notified when metrics exceed thresholds</p>
                <button className="btn btn-primary" onClick={openAddRuleModal} style={{ marginTop: 'var(--space-4)' }}>
                  <Plus size={18} /> Create Rule
                </button>
              </div>
            ) : (
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Scope</th>
                      <th>Metric</th>
                      <th>Condition</th>
                      <th>Eval Window</th>
                      <th>Notify</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rules.map((rule) => (
                      <tr key={rule.rule_id}>
                        <td style={{ fontWeight: 600 }}>{rule.name}</td>
                        <td>
                          <div style={{ fontSize: 'var(--text-xs)' }}>
                            {rule.account_id ? (
                              <code>{rule.account_id}</code>
                            ) : (
                              <span style={{ color: 'var(--slate-500)' }}>All accounts</span>
                            )}
                            {rule.resource_type && (
                              <div style={{ color: 'var(--slate-500)' }}>{rule.resource_type}</div>
                            )}
                          </div>
                        </td>
                        <td>
                          <span className="badge badge-info" style={{ textTransform: 'none' }}>{rule.metric}</span>
                        </td>
                        <td>
                          <span style={{ fontFamily: 'monospace' }}>
                            {rule.comparison === 'gt' ? '>' : rule.comparison === 'gte' ? '≥' : rule.comparison === 'lt' ? '<' : '≤'}
                            {rule.threshold}
                          </span>
                        </td>
                        <td>{rule.evaluation_minutes} min</td>
                        <td>
                          <span className="badge badge-neutral">{rule.notify_channel}</span>
                        </td>
                        <td>
                          <span className={classNames('badge', rule.enabled ? 'badge-success' : 'badge-neutral')}>
                            {rule.enabled ? 'Enabled' : 'Disabled'}
                          </span>
                        </td>
                        <td>
                          <Dropdown
                            trigger={({ onClick }) => (
                              <button className="btn btn-ghost btn-icon btn-sm" aria-label="Actions" onClick={onClick}>
                                <Edit size={16} />
                              </button>
                            )}
                          >
                            {(close) => (
                              <>
                                <button className="dropdown-item" onClick={() => { close(); openEditRuleModal(rule); }}>
                                  <Edit size={16} /> Edit
                                </button>
                                <div className="dropdown-divider" />
                                <button className="dropdown-item danger" onClick={() => { close(); handleDeleteRule(rule.rule_id); }}>
                                  <Trash2 size={16} /> Delete
                                </button>
                              </>
                            )}
                          </Dropdown>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create/Edit Rule Modal */}
      {showRuleModal && (
        <div className="modal-overlay" onClick={closeRuleModal} role="dialog" aria-modal="true" aria-labelledby="rule-modal-title">
          <div className="modal" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
            <header className="modal-header">
              <h2 id="rule-modal-title" className="modal-title">
                {editingRule ? 'Edit Alert Rule' : 'Create Alert Rule'}
              </h2>
              <button className="modal-close" onClick={closeRuleModal} aria-label="Close">
                <X size={20} />
              </button>
            </header>
            <form onSubmit={handleRuleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label htmlFor="rule-name" className="form-label">Rule Name</label>
                  <input
                    type="text"
                    id="rule-name"
                    className={classNames('form-input', { error: !!ruleFormErrors.name })}
                    value={ruleForm.name}
                    onChange={(e) => setRuleForm({ ...ruleForm, name: e.target.value })}
                    placeholder="e.g., High CPU Utilization"
                    disabled={submitting}
                  />
                  {ruleFormErrors.name && <p className="form-error">{ruleFormErrors.name}</p>}
                </div>

                <div className="form-group">
                  <label htmlFor="rule-account" className="form-label">Account (optional)</label>
                  <select
                    id="rule-account"
                    className="form-select"
                    value={ruleForm.account_id}
                    onChange={(e) => setRuleForm({ ...ruleForm, account_id: e.target.value })}
                    disabled={submitting}
                  >
                    <option value="">All Accounts</option>
                    {/* Would be populated from accounts API */}
                  </select>
                  <p className="form-hint">Leave blank to apply to all accounts</p>
                </div>

                <div className="form-group">
                  <label htmlFor="rule-resource-type" className="form-label">Resource Type (optional)</label>
                  <select
                    id="rule-resource-type"
                    className="form-select"
                    value={ruleForm.resource_type}
                    onChange={(e) => setRuleForm({ ...ruleForm, resource_type: e.target.value })}
                    disabled={submitting}
                  >
                    <option value="">All Types</option>
                    <option value="ec2-instance">EC2 Instance</option>
                    <option value="rds-instance">RDS Instance</option>
                    <option value="lambda-function">Lambda Function</option>
                    <option value="ebs-volume">EBS Volume</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="rule-metric" className="form-label">Metric</label>
                  <select
                    id="rule-metric"
                    className={classNames('form-select', { error: !!ruleFormErrors.metric })}
                    value={ruleForm.metric}
                    onChange={(e) => setRuleForm({ ...ruleForm, metric: e.target.value })}
                    disabled={submitting}
                  >
                    {METRICS.map((m) => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                  {ruleFormErrors.metric && <p className="form-error">{ruleFormErrors.metric}</p>}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
                  <div className="form-group">
                    <label htmlFor="rule-comparison" className="form-label">Comparison</label>
                    <select
                      id="rule-comparison"
                      className={classNames('form-select', { error: !!ruleFormErrors.comparison })}
                      value={ruleForm.comparison}
                      onChange={(e) => setRuleForm({ ...ruleForm, comparison: e.target.value as any })}
                      disabled={submitting}
                    >
                      {COMPARISONS.map((c) => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                    {ruleFormErrors.comparison && <p className="form-error">{ruleFormErrors.comparison}</p>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="rule-threshold" className="form-label">Threshold</label>
                    <input
                      type="number"
                      id="rule-threshold"
                      className={classNames('form-input', { error: !!ruleFormErrors.threshold })}
                      value={ruleForm.threshold}
                      onChange={(e) => setRuleForm({ ...ruleForm, threshold: Number(e.target.value) })}
                      step="0.01"
                      disabled={submitting}
                    />
                    {ruleFormErrors.threshold && <p className="form-error">{ruleFormErrors.threshold}</p>}
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
                  <div className="form-group">
                    <label htmlFor="rule-eval" className="form-label">Evaluation Window (min)</label>
                    <input
                      type="number"
                      id="rule-eval"
                      className={classNames('form-input', { error: !!ruleFormErrors.evaluation_minutes })}
                      value={ruleForm.evaluation_minutes}
                      onChange={(e) => setRuleForm({ ...ruleForm, evaluation_minutes: Number(e.target.value) })}
                      min={1}
                      max={1440}
                      disabled={submitting}
                    />
                    {ruleFormErrors.evaluation_minutes && <p className="form-error">{ruleFormErrors.evaluation_minutes}</p>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="rule-channel" className="form-label">Notification Channel</label>
                    <select
                      id="rule-channel"
                      className="form-select"
                      value={ruleForm.notify_channel}
                      onChange={(e) => setRuleForm({ ...ruleForm, notify_channel: e.target.value as any })}
                      disabled={submitting}
                    >
                      <option value="sns">SNS</option>
                      <option value="slack">Slack</option>
                      <option value="email">Email</option>
                    </select>
                  </div>
                </div>
              </div>
              <footer className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeRuleModal} disabled={submitting}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? (
                    <>
                      <Loader2 size={18} className="spin" /> Saving...
                    </>
                  ) : (
                    editingRule ? 'Save Changes' : 'Create Rule'
                  )}
                </button>
              </footer>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}